import { Empty, Tag, Typography } from 'antd';
import type { DiffResult } from '@/api/types';
import { CHANGE_CLASS_META, classifyChange } from '@/flow-ir/diff';

const { Text } = Typography;

const INTENT_VAR: Record<string, string> = {
  neutral: 'var(--ol-text-tertiary)',
  info: 'var(--ol-info)',
  attention: 'var(--ol-attention)',
  negative: 'var(--ol-negative)',
};

const STATE_SYMBOL = { added: '+', removed: '−', changed: '~' } as const;
const STATE_VAR = {
  added: 'var(--ol-positive)',
  removed: 'var(--ol-negative)',
  changed: 'var(--ol-attention)',
} as const;

/** The change-class badge (C0–C4). */
export function ChangeClassBadge({ result }: { result: DiffResult }) {
  const meta = CHANGE_CLASS_META[result.changeClass];
  return (
    <Tag
      bordered={false}
      style={{ color: INTENT_VAR[meta.intent], background: 'var(--ol-bg-subtle)', fontWeight: 700 }}
      title={`Change class ${meta.short} — ${meta.label}`}
    >
      {meta.short} · {meta.label}
      {result.semanticNoOp && ' · no-op'}
    </Tag>
  );
}

/**
 * The structured diff list (spec §9.3). Server semantic diff → per-change captions, click-to-focus
 * the changed element on the canvas. Byte-equivalent to what the registry records at publish.
 */
export function DiffPanel({
  result,
  onFocusLocus,
}: {
  result: DiffResult;
  onFocusLocus: (locus: string) => void;
}) {
  return (
    <div style={{ overflow: 'auto', height: '100%', padding: '8px 10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <ChangeClassBadge result={result} />
        <Text type="secondary" style={{ fontSize: 11.5 }} className="ol-mono">
          v{result.from.version} → v{result.to.version}
        </Text>
        <div style={{ flex: 1 }} />
        <Text type="secondary" style={{ fontSize: 11.5 }}>
          {result.changes.length} change{result.changes.length === 1 ? '' : 's'}
        </Text>
      </div>

      {result.changes.length === 0 && <Empty description="No structural differences" />}

      {result.changes.map((c, i) => {
        const state = classifyChange(c.type);
        return (
          <div
            key={i}
            className="ol-problem"
            role="button"
            tabIndex={0}
            onClick={() => onFocusLocus(c.locus)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onFocusLocus(c.locus);
            }}
          >
            <span style={{ color: STATE_VAR[state], fontWeight: 800, flex: 'none' }}>
              {STATE_SYMBOL[state]}
            </span>
            <span style={{ minWidth: 0 }}>
              <span className="ol-problem__code">{c.type}</span>
              {c.locus && <span className="ol-problem__code"> · {c.locus}</span>} {c.detail}
              <Tag
                bordered={false}
                style={{ fontSize: 9, marginInlineStart: 6, lineHeight: '16px' }}
                color={c.changeClass === 'C4_OBLIGATION' ? 'error' : undefined}
              >
                {CHANGE_CLASS_META[c.changeClass].short}
              </Tag>
            </span>
          </div>
        );
      })}
    </div>
  );
}
